<script setup lang="ts">
import { computed, ref } from 'vue'
import { institution } from '../lib/institution'
import { clock, dayShort } from '@shared/lib/schedule'
import { formatExamDuration } from '../lib/time'
import type { ExamSummary, Participant, SessionInfo, WindowState } from '../types'
import HomeShell from './HomeShell.vue'
import ChangePassword from './ChangePassword.vue'

// The participant's start page: who they are, which exam and when, the rules, and (when the
// session asks for it) the room token. Starting is only possible after the rules are accepted.
const props = defineProps<{
  participant: Participant
  exam: ExamSummary
  session?: SessionInfo
  /** The pupil's own sign-in token, for changing the password. */
  authToken?: string
  busy: boolean
  error: string
}>()

const emit = defineEmits<{ start: [token: string | undefined]; cancel: [] }>()

const token = ref('')
const accepted = ref(false)

const tokenRequired = computed(() => props.exam.token_required === true)
const canStart = computed(
  () => !props.busy && accepted.value && (!tokenRequired.value || token.value.trim().length > 0),
)

const stateLabel: Record<WindowState, string> = {
  open: 'Sedang dibuka',
  upcoming: 'Belum dimulai',
  closed: 'Sudah ditutup',
}

const schedule = computed(() => {
  const info = props.session
  if (!info || (!info.window_start && !info.window_end)) return ''
  const zone = institution.timeZone
  // Same day: "Sen 9 Nov · 07:30-08:30". Otherwise both ends with their day.
  if (info.window_start && info.window_end && dayShort(info.window_start, zone) === dayShort(info.window_end, zone)) {
    return `${dayShort(info.window_start, zone)} · ${clock(info.window_start, zone)}-${clock(info.window_end, zone)}`
  }
  const from = info.window_start ? `${dayShort(info.window_start, zone)} ${clock(info.window_start, zone)}` : 'kapan saja'
  const to = info.window_end ? `${dayShort(info.window_end, zone)} ${clock(info.window_end, zone)}` : 'tanpa batas'
  return `${from} – ${to}`
})

function start(): void {
  if (!canStart.value) return
  emit('start', tokenRequired.value ? token.value.trim().toUpperCase() : undefined)
}
</script>

<template>
  <HomeShell>
    <p class="hello">Selamat datang,</p>
    <h2 class="name">{{ participant.display_name }}</h2>

    <dl class="identity">
      <div>
        <dt>Nomor peserta</dt>
        <dd>{{ participant.login_id || participant.id }}</dd>
      </div>
      <div v-if="participant.room">
        <dt>Ruang</dt>
        <dd>{{ participant.room }}</dd>
      </div>
    </dl>

    <p v-if="error" class="banner banner--error" role="alert">{{ error }}</p>

    <div class="exam">
      <h3>{{ exam.title }}</h3>
      <dl>
        <div>
          <dt>Jumlah soal</dt>
          <dd>{{ exam.item_count }}</dd>
        </div>
        <div>
          <dt>Durasi</dt>
          <dd>{{ formatExamDuration(exam.duration_seconds) }}</dd>
        </div>
        <div v-if="session">
          <dt>Status</dt>
          <dd :class="`state state--${session.window_state}`">{{ stateLabel[session.window_state] ?? session.window_state }}</dd>
        </div>
      </dl>
      <p v-if="schedule" class="schedule">Jadwal: {{ schedule }}</p>
    </div>

    <div v-if="exam.instructions?.length" class="rules">
      <h3>Petunjuk pengerjaan</h3>
      <ol>
        <li v-for="instruction in exam.instructions" :key="instruction">{{ instruction }}</li>
      </ol>
    </div>

    <form class="start" autocomplete="off" @submit.prevent="start">
      <template v-if="tokenRequired">
        <label for="exam-token">Token ujian</label>
        <input
          id="exam-token"
          v-model="token"
          class="token-input"
          type="text"
          inputmode="text"
          maxlength="6"
          autocomplete="off"
          autocapitalize="characters"
          spellcheck="false"
          placeholder="Contoh: K7M2QX"
          :disabled="busy"
        />
        <p class="field-help">Minta token kepada pengawas di ruangan.</p>
      </template>

      <label class="accept">
        <input v-model="accepted" type="checkbox" :disabled="busy" />
        <span>Saya sudah membaca petunjuk dan akan mengerjakan dengan jujur.</span>
      </label>

      <p class="note">Waktu ujian mulai berjalan setelah Anda menekan tombol di bawah.</p>

      <button class="button button--primary button--wide" type="submit" :disabled="!canStart">
        <span v-if="busy" class="spinner" aria-hidden="true" />
        {{ busy ? 'Menyiapkan ujian…' : 'Mulai Ujian' }}
      </button>
    </form>
    <button class="link-button back" type="button" :disabled="busy" @click="emit('cancel')">
      Bukan Anda? Keluar
    </button>
    <ChangePassword v-if="authToken" :token="authToken" />
  </HomeShell>
</template>

<style scoped>
.hello {
  margin: 0;
  text-align: center;
  color: var(--muted);
  font-size: 0.9rem;
}

.name {
  margin: 2px 0 12px;
  text-align: center;
  font-size: 1.25rem;
}

.identity {
  display: flex;
  justify-content: center;
  gap: 28px;
  margin: 0 0 16px;
}

.identity dt,
.exam dt {
  font-size: 0.78rem;
  color: var(--muted);
}

.identity dd,
.exam dd {
  margin: 0;
  font-weight: 700;
}

.exam {
  padding: 14px 16px;
  border: 1px solid var(--line);
  border-radius: 12px;
  background: var(--surface-soft);
}

.exam h3,
.rules h3 {
  margin: 0 0 8px;
  font-size: 1rem;
}

.exam dl {
  display: flex;
  flex-wrap: wrap;
  gap: 12px 28px;
  margin: 0;
}

.schedule {
  margin: 10px 0 0;
  font-size: 0.85rem;
  color: var(--ink-soft);
}

.state--open {
  color: var(--success);
}

.rules {
  margin-top: 16px;
  font-size: 0.9rem;
}

.rules ol {
  margin: 0;
  padding-left: 20px;
  color: var(--ink-soft);
}

.start {
  display: grid;
  gap: 6px;
  margin-top: 16px;
}

.start label {
  font-size: 0.85rem;
  font-weight: 600;
  color: var(--ink-soft);
}

.token-input {
  width: 100%;
  height: 50px;
  padding: 0 14px;
  font-size: 1.25rem;
  letter-spacing: 0.25em;
  text-align: center;
  text-transform: uppercase;
}

.accept {
  display: flex;
  align-items: flex-start;
  gap: 10px;
  margin-top: 8px;
  font-weight: 400;
}

.accept input {
  margin-top: 3px;
}

.note {
  margin: 8px 0;
  font-size: 0.85rem;
  color: var(--muted);
}

.back {
  display: block;
  margin: 12px auto 0;
}
</style>
